import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useAtom } from "jotai";
import {
    DictType, Scene, VariableData,
    ScenarioAutocompletes, ScenarioAutocomplete, SceneTemplate, Paragraph
} from "@/model";
import { userVariablesAtom } from "@/atom/userVariables";
import { useTemplates } from "@/query/template";
import { useScenes } from "@/query/scene";
import { useScenarioAutocompletes as useScenarioAutocompletesQuery } from "@/query/scenario";
import { useScenarioId } from "@/hook/useScenarioId";


const TEMPLATE_AUTOCOMPLETE: ScenarioAutocomplete[] = [
    { value: "{{ message }}", output: "{{ message }}" },
    { value: "{{ intent.name }}", output: "{{ intent.name }}" },
    { value: "{{ entity.names }}", output: "{{ entity.names }}" },
    { value: "{{ display_condition.match_count }}", output: "{{ display_condition.match_count }}" },
    { value: "{{ intent_duplicate_count }}", output: "{{ intent_duplicate_count }}" },
    { value: "{{ intent_failure_count }}", output: "{{ intent_failure_count }}" },
    { value: "{{ }}", output: "{{ }}" },
    { value: "''", output: "''" },
    { value: "['','']", output: "['','']" },
];

export const useScenarioAutocompletes = () => {
    const intl = useIntl();
    const scenarioId = useScenarioId();

    const [userVariables, setUserVariables] = useAtom(userVariablesAtom);

    const { data: scenarioAutocompletes } = useScenarioAutocompletesQuery(scenarioId);
    const { data: templates } = useTemplates(scenarioId);
    const { data: scenes } = useScenes(scenarioId);

    const templateVariables = useMemo(() => (
        getVariables(templates?.flatMap(template => template.paragraphs ?? []) ?? [])
    ), [templates]);

    const sceneVariables = useMemo(() => (
        getVariables(scenes?.flatMap(scene => scene.paragraphs ?? []) ?? [])
    ), [scenes]);

    const setCurrentUserVariables = useCallback((type: "scene" | "template", data: Scene[] | SceneTemplate[]) => {
        if (type === "scene") {
            return setUserVariables(Array.from(new Set([...templateVariables, ...getVariables(data.flatMap(scene => scene.paragraphs ?? []))])).sort());
        }
        if (type === "template") {
            return setUserVariables(Array.from(new Set([...sceneVariables, ...getVariables(data.flatMap(template => template.paragraphs ?? []))])).sort());
        }
    }, [setUserVariables, sceneVariables, templateVariables]);

    const clearUserVariables = useCallback(() => {
        setUserVariables(undefined);
    }, [setUserVariables]);

    const fullAutocomplete: DictType<ScenarioAutocompletes> = useMemo(() => {
        return {
            "{": {
                autocompletes: TEMPLATE_AUTOCOMPLETE,
            },
            "#": {
                autocompletes: toIntentAutocomplete(scenarioAutocompletes?.intent ?? []),
                errorText: intl.formatMessage({ id: "i00032" }),
            },
            "@": {
                autocompletes: toEntityAutocomplete(scenarioAutocompletes?.entity ?? {}),
                errorText: intl.formatMessage({ id: "i00032" }),
            },
            $: {
                autocompletes: toVariableAutocomplete(
                    scenarioAutocompletes?.variable.constants ?? [],
                    userVariables ? userVariables : scenarioAutocompletes?.variable.user ?? [],
                    scenarioAutocompletes?.variable.system ?? []
                ),
            }
        };
    }, [intl, scenarioAutocompletes, userVariables]);

    const variableAutocomplete: DictType<ScenarioAutocompletes> = useMemo(() => {
        return {
            "{": {
                autocompletes: toVariableAutocomplete(
                    scenarioAutocompletes?.variable.constants ?? [],
                    userVariables ? userVariables : scenarioAutocompletes?.variable.user ?? [],
                    scenarioAutocompletes?.variable.system ?? []
                )
            }
        };
    }, [scenarioAutocompletes, userVariables]);

    const intentAutocomplete: DictType<ScenarioAutocompletes> = useMemo(() => {
        return {
            "#": {
                autocompletes: scenarioAutocompletes?.intent.map(intent => ({ value: intent, output: intent })) ?? [],
                errorText: intl.formatMessage({ id: "i00032" }),
            }
        };
    }, [intl, scenarioAutocompletes]);

    return { fullAutocomplete, variableAutocomplete, intentAutocomplete, setUserVariables: setCurrentUserVariables, clearUserVariables };
};

const getVariables = (paragraphs: Paragraph[]) => {
    const variables = new Set<string>();
    paragraphs.forEach(p => {
        if (p.data_type !== "variable" || !p.data) {
            return;
        }
        const { global, local } = p.data as VariableData;
        Object.keys(global ?? {}).forEach(key => variables.add(key));
        Object.keys(local ?? {}).forEach(key => variables.add(key));
    });
    return Array.from(variables).sort();
};

const removeSpace = (value: string) => value.replace(/ /g, "");

const toIntentAutocomplete = (intents: string[]) => {
    return intents.map(intent => ({ value: intent, output: `{{ intent.name }} == '${intent}' ` }));
};

const toEntityAutocomplete = (entityObj: DictType<string[]>) => {
    return Object.entries(entityObj).flatMap(([entity, entityValues]) => (
        [
            { value: entity, output: `{{ entity.names }} == '${entity}' ` },
            ...entityValues.map(
                entityValue => {
                    const refined = removeSpace(entityValue);
                    return { value: `${entity}:${refined}`, output: `{{ entity.${entity}.keyword }} == '${refined}' ` };
                }
            )
        ]
    ));
};

const toVariableAutocomplete = (system: string[], user: string[], constants: string[]) => {
    return Array.from(new Set([...system, ...user, ...constants])).sort().map(v => ({ value: v, output: `{{ ${v} }}` }));
};

